/**
 * EditableText — Content-override system for microsite copy.
 *
 * Usage:
 *   <EditableContentProvider ns="portfolio" editMode={isAdmin}>
 *     <E k="hero.headline" defaultText="The headline" as="h1" className="..." />
 *     <E k="hero.body" defaultText="The body" as="p" multiline />
 *   </EditableContentProvider>
 *
 * In read-mode, renders the override (if any) or the default text.
 * In edit-mode, the element is contenteditable; on blur, saves via PATCH
 * /api/content/{ns}. A small toolbar appears with a "revert to default" action.
 *
 * Edits are debounced so rapid typing only fires one save per ~600ms.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { RotateCcw, CheckCircle2 } from "lucide-react";
import { getContentOverrides, setContentOverride, resetContentOverride } from "@/lib/api";

const Ctx = createContext({ overrides: {}, ns: null, editMode: false, refresh: () => {}, save: () => {} });

export function useEditableContent() { return useContext(Ctx); }

export function EditableContentProvider({ ns, editMode = false, children }) {
  const [overrides, setOverrides] = useState({});

  const refresh = useCallback(async () => {
    try {
      const r = await getContentOverrides(ns);
      setOverrides(r?.overrides || {});
    } catch (e) {
    }
  }, [ns]);

  useEffect(() => { refresh(); }, [refresh]);

  const save = useCallback(async (key, value) => {
    try {
      await setContentOverride(ns, key, value);
      setOverrides((prev) => ({ ...prev, [key]: value }));
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
      return false;
    }
  }, [ns]);

  const reset = useCallback(async (key) => {
    try {
      await resetContentOverride(ns, key);
      setOverrides((prev) => { const next = { ...prev }; delete next[key]; return next; });
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Revert failed");
      return false;
    }
  }, [ns]);

  const ctxValue = useMemo(() => ({ overrides, ns, editMode, refresh, save, reset }), [overrides, ns, editMode, refresh, save, reset]);
  return <Ctx.Provider value={ctxValue}>{children}</Ctx.Provider>;
}

/**
 * <E k="some.key" defaultText="..." as="h1" className="..." multiline={false} html={false} />
 *
 * - `as` chooses the DOM tag (h1, h2, p, span, div). Default: span.
 * - `multiline` allows Enter to insert newlines (otherwise blur on Enter).
 * - `html` is reserved (not yet supported); always plain text for safety.
 */
export function E({ k, defaultText = "", as = "span", className = "", multiline = false, style }) {
  const { overrides, editMode, save, reset } = useEditableContent();
  const value = overrides[k] !== undefined ? overrides[k] : defaultText;
  const isOverridden = overrides[k] !== undefined;
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const saveTimer = useRef(null);

  // Keep DOM text in sync if override changes externally
  useEffect(() => {
    if (!editMode) return;
    if (ref.current && ref.current.textContent !== value) {
      ref.current.textContent = value;
    }
  }, [value, editMode]);

  const Tag = as;

  if (!editMode) {
    // Read mode — plain render. Preserves whitespace for paragraph copy.
    return <Tag className={className} style={style}>{value}</Tag>;
  }

  const flash = () => {
    setShowSaved(true);
    setTimeout(() => setShowSaved(false), 1200);
  };

  const onInput = () => {
    if (!ref.current) return;
    const next = ref.current.textContent || "";
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      if (next === value) return;
      setBusy(true);
      const ok = await save(k, next);
      setBusy(false);
      if (ok) flash();
    }, 600);
  };

  const onBlur = async () => {
    if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = null; }
    if (!ref.current) return;
    const next = ref.current.textContent || "";
    if (next === value) return;
    setBusy(true);
    const ok = await save(k, next);
    setBusy(false);
    if (ok) flash();
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !multiline) {
      e.preventDefault();
      ref.current?.blur();
    }
    if (e.key === "Escape") {
      e.preventDefault();
      if (ref.current) ref.current.textContent = value;
      ref.current?.blur();
    }
  };

  const handleRevert = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setBusy(true);
    const ok = await reset(k);
    setBusy(false);
    if (ok) {
      toast.success("Reverted to default");
      if (ref.current) ref.current.textContent = defaultText;
    }
  };

  const isInline = as === "span";
  const wrapperStyle = { display: isInline ? "inline-block" : "block", width: isInline ? undefined : "100%" };
  const tagStyle = {
    ...(style || {}),
    background: isOverridden ? "rgba(245,180,0,0.08)" : "transparent",
    boxShadow: "inset 0 0 0 1px rgba(245,180,0,0.20)",
    padding: "0.05em 0.2em",
    margin: "-0.05em -0.2em",
    cursor: "text",
  };

  return (
    <span className="relative group inline-block align-baseline" style={wrapperStyle}>
      <Tag
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={onInput}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        className={`${className} outline-none transition rounded-sm`}
        style={tagStyle}
        data-testid={`editable-${k}`}
        spellCheck={true}
      >{value}</Tag>
      <span className="absolute -top-3 -right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition pointer-events-auto no-print" style={{ zIndex: 30 }}>
        {showSaved && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-medium" style={{ background: "#16a34a", color: "#fff" }}>
            <CheckCircle2 className="h-2.5 w-2.5" /> saved
          </span>
        )}
        {busy && (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium" style={{ background: "#0F1E2E", color: "#FDFBF5" }}>
            saving…
          </span>
        )}
        {isOverridden && (
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleRevert}
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-medium hover:opacity-80"
            style={{ background: "#0F1E2E", color: "#FDFBF5" }}
            title="Revert to default"
            data-testid={`revert-${k}`}
          >
            <RotateCcw className="h-2.5 w-2.5" /> revert
          </button>
        )}
      </span>
    </span>
  );
}
